// Dev preview for the 18+ Career Navigator report.
// URL: /report/18-plus/preview?seed=1&name=Elmira&lang=ru
//
// Generates a deterministic Result1822 from a small RNG seeded by `seed` so the
// same URL always produces the same report. No real diagnostic answers needed —
// this is for visual QA after Phase 3 changes.

import { createFileRoute, Link } from "@tanstack/react-router";
import Report13Page from "@/components/report/Report13Page";
import type { Lang1822 } from "@/lib/reports1822";
import { buildMockResult1822 } from "@/lib/preview-mock-1822";

type PreviewSearch = {
  seed: number;
  name: string;
  age: number;
  lang: Lang1822;
  unreliable?: boolean;
};

export const Route = createFileRoute("/report/18-plus/preview")({
  validateSearch: (search: Record<string, unknown>): PreviewSearch => ({
    seed:
      typeof search.seed === "number"
        ? search.seed
        : typeof search.seed === "string"
          ? parseInt(search.seed, 10) || 1
          : 1,
    name: typeof search.name === "string" && search.name ? search.name : "Elmira",
    age:
      typeof search.age === "number"
        ? search.age
        : typeof search.age === "string"
          ? parseInt(search.age, 10) || 22
          : 22,
    lang: (typeof search.lang === "string" && ["ru", "az", "en"].includes(search.lang)
      ? search.lang
      : "ru") as Lang1822,
    // ?unreliable=1 — плоский профиль: полный отчёт с пометкой на стр. 2 (13.10).
    unreliable: search.unreliable === "1" || search.unreliable === 1 || search.unreliable === true,
  }),
  head: () => ({
    meta: [{ title: "Yolutap · Отчёт 18+ — предпросмотр" }, { name: "robots", content: "noindex" }],
  }),
  component: PreviewPage,
});

function PreviewPage() {
  const { seed, name, age, lang, unreliable } = Route.useSearch();
  const result = buildMockResult1822(seed, lang);
  if (unreliable) result.confidence = "unreliable";
  const date = new Date();

  return (
    <>
      {/*
        Print scope: strip the dev chrome (toolbar + cream-card framing).
        Without this, the outer padding/minHeight and the toolbar height
        push .report ~70px down, and Chrome adds a leading blank paper
        page (toolbar takes paper page 1) plus a trailing blank from the
        24px bottom padding — extra paper pages beyond the designed count.
        Same fix as report.14-15.preview.tsx.
      */}
      <style>{`
        @media print {
          .preview-frame { padding: 0 !important; background: transparent !important; min-height: 0 !important; }
          .preview-toolbar { display: none !important; }
          .preview-card { box-shadow: none !important; max-width: none !important; margin: 0 !important; }
        }
      `}</style>
      <div className="preview-frame" style={{ padding: 24, background: "#E7E2D6", minHeight: "100vh" }}>
        <div
          className="preview-toolbar"
          style={{
            maxWidth: 900,
            margin: "0 auto",
            marginBottom: 20,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Link
            to="/report/18-plus/preview"
            search={(prev) => ({
              ...prev,
              seed: (typeof prev?.seed === "number" ? prev.seed : 1) + 1,
              name,
              age,
              lang,
            })}
            style={{
              background: "#1E5A6B",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: 6,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            ↻ Новый случайный (seed +1)
          </Link>
          <Link
            to="/report/18-plus/preview"
            search={{ seed, name, age, lang: "ru" }}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: lang === "ru" ? "#1E5A6B" : "#fff",
              color: lang === "ru" ? "#fff" : "#1E5A6B",
              textDecoration: "none",
              border: "1px solid #1E5A6B",
              fontSize: 13,
            }}
          >
            RU
          </Link>
          <Link
            to="/report/18-plus/preview"
            search={{ seed, name, age, lang: "az" }}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: lang === "az" ? "#1E5A6B" : "#fff",
              color: lang === "az" ? "#fff" : "#1E5A6B",
              textDecoration: "none",
              border: "1px solid #1E5A6B",
              fontSize: 13,
            }}
          >
            AZ
          </Link>
          <Link
            to="/report/18-plus/preview"
            search={{ seed, name, age, lang: "en" }}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: lang === "en" ? "#1E5A6B" : "#fff",
              color: lang === "en" ? "#fff" : "#1E5A6B",
              textDecoration: "none",
              border: "1px solid #1E5A6B",
              fontSize: 13,
            }}
          >
            EN
          </Link>
          <span style={{ fontSize: 12, color: "#6B8090", marginLeft: "auto" }}>
            seed={seed} · RIASEC={result.riasecCode} · clusters=[
            {result.topClusters.map((c) => c.id).join(", ")}]
          </span>
        </div>

        <div
          className="preview-card"
          style={{
            background: "#fff",
            boxShadow: "0 24px 48px -24px rgba(15,42,56,.25)",
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          <Report13Page name={name} age={age} result={result} date={date} lang={lang} />
        </div>
      </div>
    </>
  );
}
